import { addCalendarDays, isoWeekNumber } from '@pulsify/shared'
import type { IsoWeekday, LunchMenuDto, MenuDiet, OrganizationProfile } from '@pulsify/shared/dto'
import type { FlyerContent, FlyerLocale } from '~/features/events/checkin-flyer/flyer-content'
import { weekdayLabel } from '~/features/listing-menus/lib/menu-format'
import * as m from '~/paraglide/messages'
import type { Listing } from '~/shared/types'

export type PrintSize = 'a4' | 'a5' | 'a6'

export const PRINT_SIZES_MM: Record<PrintSize, { width: number; height: number }> = {
  a4: { width: 210, height: 297 },
  a5: { width: 148, height: 210 },
  a6: { width: 105, height: 148 },
}

export type FlyerTone = 'color' | 'white'

/** The place profile that is this listing, whose share links its printed QR codes use. */
export function placeProfileOf(
  profiles: OrganizationProfile[],
  listingId: string,
): OrganizationProfile | null {
  return (
    profiles.find((profile) => profile.kind === 'place' && profile.listingId === listingId) ?? null
  )
}

export function buildFollowFlyerContent(
  listing: Listing,
  url: string,
  locale: FlyerLocale,
): FlyerContent {
  const options = { locale }
  return {
    motif: 'onsite',
    title: listing.name,
    when: '',
    venue: [listing.location.street, listing.location.city].filter(Boolean).join(', '),
    coverUrl: listing.coverImageUrl || listing.images[0]?.url || null,
    code: '',
    url,
    headline: m.placeFollowFlyerHeadline({}, options),
    tagline: m.placeFollowFlyerTagline({ venueName: listing.name }, options),
    fineprint: m.placeFollowFlyerFineprint({}, options),
    codeLabel: '',
  }
}

export interface LunchFlyerDish {
  key: string
  name: string
  description: string | null
  price: string | null
  diets: string | null
}

export interface LunchFlyerDay {
  weekday: IsoWeekday
  label: string
  dishes: LunchFlyerDish[]
}

export interface LunchFlyerContent {
  title: string
  placeName: string
  period: string
  note: string | null
  days: LunchFlyerDay[]
  url: string
  scanHint: string
  density: 'roomy' | 'regular' | 'compact'
}

const DIET_LABELS: Record<MenuDiet, (locale: FlyerLocale) => string> = {
  vegetarian: (locale) => m.menuDietVegetarian({}, { locale }),
  vegan: (locale) => m.menuDietVegan({}, { locale }),
}

function shortDate(date: string, locale: FlyerLocale): string {
  return new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: locale === 'de' ? 'numeric' : 'short',
    timeZone: 'UTC',
  }).format(new Date(`${date}T12:00:00Z`))
}

function lunchPeriod(menu: LunchMenuDto, weekdays: IsoWeekday[], locale: FlyerLocale): string {
  if (!menu.weekOf) return m.placeLunchFlyerEveryWeek({}, { locale })
  const week = m.placeLunchFlyerWeek({ week: isoWeekNumber(menu.weekOf) }, { locale })
  const first = weekdays[0]
  const last = weekdays.at(-1)
  if (!first || !last) return week
  const from = shortDate(addCalendarDays(menu.weekOf, first - 1), locale)
  const to = shortDate(addCalendarDays(menu.weekOf, last - 1), locale)
  return `${week} · ${from === to ? from : `${from}–${to}`}`
}

/** The whole week of a Mittagsmenü as printed on one sheet, sized down as the dishes add up. */
export function buildLunchFlyerContent(
  listing: Listing,
  menu: LunchMenuDto,
  url: string,
  locale: FlyerLocale,
): LunchFlyerContent {
  const price = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' })
  const days = menu.days
    .filter((day) => day.dishes.length > 0)
    .sort((a, b) => a.weekday - b.weekday)
    .map((day) => ({
      weekday: day.weekday,
      label: weekdayLabel(day.weekday, locale, 'long'),
      dishes: day.dishes.map((dish, index) => ({
        key: `${day.weekday}-${index}`,
        name: dish.name,
        description: dish.description,
        price: dish.priceCents === null ? null : price.format(dish.priceCents / 100),
        diets: dish.diets.length
          ? dish.diets.map((diet) => DIET_LABELS[diet](locale)).join(' · ')
          : null,
      })),
    }))
  const lines = days.reduce((sum, day) => sum + 1 + day.dishes.length, 0)

  return {
    title: menu.title ?? m.menuLunchTitle({}, { locale }),
    placeName: listing.name,
    period: lunchPeriod(
      menu,
      days.map((day) => day.weekday),
      locale,
    ),
    note: menu.note,
    days,
    url,
    scanHint: m.placeLunchFlyerScan({ venueName: listing.name }, { locale }),
    density: lines <= 12 ? 'roomy' : lines <= 24 ? 'regular' : 'compact',
  }
}
